import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, BarChart3, CheckCircle, CreditCard, Euro, PieChart, Receipt, RefreshCw, TrendingDown, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BACKEND_URL } from './constants';
import { getToken } from '../../services/tokenService';

export default function KPIDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/kpi/dashboard`, { headers });
      setData(res.data);
    } catch { toast.error('Erreur chargement KPI'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-orange-400" /></div>;
  if (!data) return <div className="text-center py-16 text-gray-500">Erreur chargement des donnees</div>;

  const { resume, statuts, modes_paiement, echeances_retard, encaissements_mensuels, top_retard } = data;
  const maxMensuel = Math.max(...encaissements_mensuels.map(m => m.montant), 1);

  const modeLabels = {
    virement: 'Virement', cb: 'Carte bancaire', stripe: 'Stripe', sepa_core: 'SEPA CORE',
    sepa_b2b: 'SEPA B2B', cheque: 'Cheque', especes: 'Especes', lien_paiement: 'Lien', autre: 'Autre',
  };
  const statutLabels = { acquitte: 'Acquitte', partiellement_paye: 'Partiel', non_paye: 'Non paye' };
  const statutColors = { acquitte: '#059669', partiellement_paye: '#f59e0b', non_paye: '#6b7280' };

  return (
    <div className="space-y-6" data-testid="kpi-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="kpi-card-recus">
          <div className="flex items-center gap-3 mb-2"><div className="p-2 bg-blue-100 rounded-xl"><Receipt className="h-5 w-5 text-blue-600" /></div><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recus ESS</span></div>
          <p className="text-3xl font-bold text-gray-900">{resume.nb_recus}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="kpi-card-emis">
          <div className="flex items-center gap-3 mb-2"><div className="p-2 bg-orange-100 rounded-xl"><Euro className="h-5 w-5 text-orange-600" /></div><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total emis</span></div>
          <p className="text-2xl font-bold text-gray-900">{resume.total_emis.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-sm font-normal text-gray-400">EUR</span></p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="kpi-card-paye">
          <div className="flex items-center gap-3 mb-2"><div className="p-2 bg-emerald-100 rounded-xl"><TrendingUp className="h-5 w-5 text-emerald-600" /></div><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total paye</span></div>
          <p className="text-2xl font-bold text-emerald-700">{resume.total_paye.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-sm font-normal text-gray-400">EUR</span></p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="kpi-card-reste">
          <div className="flex items-center gap-3 mb-2"><div className="p-2 bg-red-100 rounded-xl"><TrendingDown className="h-5 w-5 text-red-500" /></div><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reste a encaisser</span></div>
          <p className="text-2xl font-bold text-red-600">{resume.total_reste.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-sm font-normal text-gray-400">EUR</span></p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 shadow-lg text-white" data-testid="kpi-card-taux">
          <div className="flex items-center gap-3 mb-2"><div className="p-2 bg-white/20 rounded-xl"><PieChart className="h-5 w-5" /></div><span className="text-xs font-medium text-white/80 uppercase tracking-wide">Taux recouvrement</span></div>
          <p className="text-3xl font-bold">{resume.taux_recouvrement}%</p>
          <div className="w-full bg-white/20 rounded-full h-2 mt-2"><div className="bg-white h-2 rounded-full transition-all" style={{ width: `${Math.min(100, resume.taux_recouvrement)}%` }} /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Encaissements mensuels (chart) */}
        <div className="lg:col-span-2 bg-white rounded-2xl border p-6 shadow-sm" data-testid="kpi-chart-mensuel">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-orange-500" /> Encaissements mensuels (6 mois)</h4>
          <div className="flex items-end gap-3 h-48">
            {encaissements_mensuels.map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`kpi-bar-${i}`}>
                <span className="text-xs font-semibold text-gray-700">{m.montant > 0 ? `${m.montant.toLocaleString('fr-FR')}` : ''}</span>
                <div className="w-full relative" style={{ height: '160px' }}>
                  <div className="absolute bottom-0 w-full rounded-t-lg bg-gradient-to-t from-orange-500 to-amber-400 transition-all duration-500"
                    style={{ height: `${Math.max(4, (m.montant / maxMensuel) * 100)}%` }} />
                </div>
                <span className="text-[10px] text-gray-400 font-medium">{m.label}</span>
                <span className="text-[10px] text-gray-300">{m.nb_paiements}p</span>
              </div>
            ))}
          </div>
        </div>

        {/* Repartition par statut */}
        <div className="bg-white rounded-2xl border p-6 shadow-sm" data-testid="kpi-statuts">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><PieChart className="h-4 w-4 text-orange-500" /> Repartition par statut</h4>
          <div className="space-y-3">
            {Object.entries(statuts.repartition).map(([key, count]) => {
              const pct = resume.nb_recus > 0 ? (count / resume.nb_recus * 100) : 0;
              return (
                <div key={key} data-testid={`kpi-statut-${key}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{statutLabels[key] || key}</span>
                    <span className="text-gray-500">{count} <span className="text-gray-300">({pct.toFixed(0)}%)</span></span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: statutColors[key] || '#6b7280' }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{(statuts.montants[key] || 0).toLocaleString('fr-FR')} EUR</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Echeances en retard */}
        <div className="bg-white rounded-2xl border p-6 shadow-sm" data-testid="kpi-retard">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /> Echeances en retard</h4>
            {echeances_retard.total > 0 && <Badge className="bg-red-700 text-white text-xs font-semibold">{echeances_retard.total} echeance{echeances_retard.total > 1 ? 's' : ''} - {echeances_retard.montant_total.toLocaleString('fr-FR')} EUR</Badge>}
          </div>
          {echeances_retard.items.length === 0 ? (
            <div className="text-center py-8 text-gray-400"><CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-300" /><p className="text-sm font-medium text-emerald-600">Aucune echeance en retard</p></div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {echeances_retard.items.map((e, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-red-50/50 border border-red-100" data-testid={`kpi-retard-${i}`}>
                  <div className="p-2 bg-red-100 rounded-lg shrink-0"><AlertTriangle className="h-4 w-4 text-red-500" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{e.raison_sociale}</p>
                    <p className="text-xs text-gray-500">{e.numero_recu} - Ech. N{e.numero_echeance} du {e.date_echeance}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-red-600">{e.montant.toLocaleString('fr-FR')} EUR</p>
                    <Badge className="bg-red-700 text-white font-semibold">{e.jours_retard}j retard</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modes de paiement + Top retard */}
        <div className="space-y-6">
          {/* Modes de paiement */}
          <div className="bg-white rounded-2xl border p-6 shadow-sm" data-testid="kpi-modes">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><CreditCard className="h-4 w-4 text-orange-500" /> Repartition par mode de paiement</h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(modes_paiement).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border" data-testid={`kpi-mode-${key}`}>
                  <span className="text-xs font-medium text-gray-700">{modeLabels[key] || key}</span>
                  <div className="text-right">
                    <span className="text-xs font-bold text-orange-700">{val.montant.toLocaleString('fr-FR')} EUR</span>
                    <span className="text-[10px] text-gray-400 block">{val.count} recu{val.count > 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top beneficiaires en retard */}
          {top_retard.length > 0 && (
            <div className="bg-white rounded-2xl border p-6 shadow-sm" data-testid="kpi-top-retard">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-red-500" /> Top beneficiaires en retard</h4>
              <div className="space-y-2">
                {top_retard.map((b, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50" data-testid={`kpi-top-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-5">{i + 1}.</span>
                      <span className="text-sm font-medium text-gray-800">{b.raison_sociale}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-red-600">{b.montant_retard.toLocaleString('fr-FR')} EUR</span>
                      <span className="text-[10px] text-gray-400 block">{b.nb_echeances} ech.</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Refresh button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} className="text-xs text-gray-500"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser</Button>
      </div>
    </div>
  );
}

